<script lang="ts">
  import Am5Loader from '@/Util/Am5Loader.svelte';
  import { onDestroy } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { getXertAm5, type am5Root } from '@/Util/XertAm5';

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // forecast_history of a report: what each saved version of the plan predicted for the end, over time.
  export let versions: { version_id: string; saved_at: string; target_date: string; destination: Record<string, number | null> }[] = [];
  export let segments: string[][] = [];   // version ids that share a target date
  export let metric = 'TP';
  export let unit = 'W';
  export let scale = 1;                  // 0.001 shows J as kJ
  export let target: { original?: number | null; adjusted?: number | null } = {};
  export let actual: { date: string; value: number } | null = null;

  const chartId = `forecast-journey-${Math.random().toString(36).slice(2)}`;
  let element: HTMLElement;
  let root: am5Root | undefined;
  const COLORS = ['#2563EB', '#0E9F6E', '#D97706', '#9333EA'];

  $: if (element && versions) rebuild(versions, segments, metric, target, actual);

  // The versions of a plan are often saved within minutes of each other, so they are laid out one after the other
  // (in the order they were saved) rather than at their place on a calendar: a time axis would stack them in one line.
  const shortDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const shortTime = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  function rebuild(...args: unknown[]) {
    void args;
    root?.dispose();
    build();
  }

  function build() {
    const { am5, am5xy, MyTheme } = getXertAm5();
    am5.addLicense('AM5C87942260');
    root = am5.Root.new(element);
    root.setThemes([MyTheme.new(root)]);
    const chart = root.container.children.push(am5xy.XYChart.new(root, { paddingLeft: 0, paddingRight: 12, layout: root.verticalLayout }));

    // One row per saved version (in order), and a last row for what the athlete reached.
    const ordered = segments.flat().map((id) => versions.find((v) => v.version_id === id)).filter((v) => v) as typeof versions;
    const dateCount: Record<string, number> = {};
    ordered.forEach((v) => { dateCount[shortDate(v.saved_at)] = (dateCount[shortDate(v.saved_at)] ?? 0) + 1; });
    const rows: Record<string, any>[] = ordered.map((v) => {
      const seg = segments.findIndex((ids) => ids.includes(v.version_id));
      const value = v.destination[metric];
      return { key: v.version_id, label: dateCount[shortDate(v.saved_at)] > 1 ? `${shortDate(v.saved_at)} ${shortTime(v.saved_at)}` : shortDate(v.saved_at), version: v.version_id, ['s' + seg]: typeof value === 'number' ? value * scale : undefined };
    });
    if (actual) rows.push({ key: 'result', label: $_('report.chart.result'), reached: actual.value * scale });

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 40, cellStartLocation: 0.1, cellEndLocation: 0.9 });
    xRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color('#6B7280') });
    xRenderer.grid.template.setAll({ strokeOpacity: 0.08 });
    // The category is the version (unique even when two are saved in the same minute); the date is what is shown.
    xRenderer.labels.template.adapters.add('text', (text: string | undefined, target: any) => target.dataItem?.dataContext?.label ?? text);
    const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, { categoryField: 'key', renderer: xRenderer }));
    xAxis.data.setAll(rows);
    const yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.grid.template.setAll({ strokeOpacity: 0.08, strokeDasharray: [3, 4] });
    yRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color('#6B7280') });

    const values: number[] = [];
    rows.forEach((r) => Object.entries(r).forEach(([k, v]) => { if ((/^s\d+$/.test(k) || k === 'reached') && typeof v === 'number') values.push(v); }));
    [target.original, target.adjusted].forEach((t) => { if (typeof t === 'number') values.push(t * scale); });
    const lo = values.length ? Math.min(...values) : 0;
    const hi = values.length ? Math.max(...values) : 1;
    const pad = Math.max((hi - lo) * 0.15, Math.abs(hi) * 0.01, 0.5);
    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: yRenderer, min: lo - pad, max: hi + pad, strictMinMax: true }));
    yAxis.ghostLabel.set('minWidth', 36);

    segments.forEach((ids, i) => {
      const first = versions.find((v) => v.version_id === ids[0]);
      const name = segments.length > 1 && first ? $_('report.chart.target_date', { values: { date: new Date(first.target_date.slice(0, 10) + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) } }) : $_('report.chart.forecast');
      const color = COLORS[i % COLORS.length];
      const s = chart.series.push(am5xy.LineSeries.new(root!, { name, xAxis, yAxis, categoryXField: 'key', valueYField: 's' + i, stroke: am5.color(color), fill: am5.color(color),
        connect: false, tooltip: am5.Tooltip.new(root!, { labelText: `{valueY.formatNumber('#.0')} ${unit} (forecast {version})` }) }));
      s.strokes.template.setAll({ strokeWidth: 2.5 });
      s.bullets.push(() => am5.Bullet.new(root!, { sprite: am5.Circle.new(root!, { radius: 4.5, fill: am5.color(color) }) }));
      s.data.setAll(rows);
    });

    // The target the plan was made for, and the one it was adjusted to when that differs.
    const range = (value: number | null | undefined, label: string, dashed: boolean) => {
      if (typeof value !== 'number') return;
      const item = yAxis.makeDataItem({ value: value * scale });
      yAxis.createAxisRange(item);
      item.get('grid')?.setAll({ stroke: am5.color('#6B7280'), strokeOpacity: 0.7, strokeDasharray: dashed ? [5, 4] : [], visible: true });
      item.get('label')?.setAll({ text: label, visible: true, fontSize: 11, fill: am5.color('#6B7280'), inside: true, centerY: am5.p100 });
    };
    range(target.original, $_('report.chart.original_target'), false);
    if (typeof target.adjusted === 'number' && target.adjusted !== target.original) range(target.adjusted, $_('report.chart.adjusted_target'), true);

    if (actual) {
      const a = chart.series.push(am5xy.LineSeries.new(root, { name: $_('report.chart.reached'), xAxis, yAxis, categoryXField: 'key', valueYField: 'reached', stroke: am5.color('#00C853') }));
      a.strokes.template.set('visible', false);
      a.bullets.push(() => am5.Bullet.new(root!, { sprite: am5.Rectangle.new(root!, { width: 11, height: 11, rotation: 45, centerX: am5.p50, centerY: am5.p50, fill: am5.color('#00C853'),
        tooltipText: `What you reached: {valueY.formatNumber('#.0')} ${unit}` }) }));
      a.data.setAll(rows);
    }

    chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none', xAxis }));
    const legend = chart.children.push(am5.Legend.new(root, { centerX: am5.p50, x: am5.p50, marginTop: 8 }));
    legend.labels.template.setAll({ fontSize: 12 });
    legend.data.setAll(chart.series.values);
  }

  onDestroy(() => root?.dispose());
</script>

<Am5Loader>
  <div id={chartId} bind:this={element} class="w-full h-64" />
</Am5Loader>
